import React, { useEffect, useMemo, useState } from 'react';
import { getScrollMapsColorForPageView, ScrollMapsColorMap } from '../utils/colormap';
import { ScrollMapsMarker } from '../ScrollMapsMarker';
import { ScrollMapsFacet } from '../utils/facetdata';
import { Bounds, PlaybackScrollElementBounds } from '../types';
import { createScaledOffsets, InverseScales } from '../utils/scales';
import { shortNumber } from '@fullstory/util/fmt';

export interface ScrollMapsMarkerContainerProps {
  /**
   * The map responsible for producing the Scroll Maps colors.
   */
  colorMap: ScrollMapsColorMap;
  /**
   * The current response from the network.
   */
  facetData: ScrollMapsFacet;
  /**
   * The bounds for the overlay container
   */
  overlayContainerBounds: Bounds;
  /**
   * The bounds for the page itself
   */
  pageBounds: Bounds;
  /**
   * Details about the scrolling element and the player viewport.
   */
  playbackScrollElementBounds: PlaybackScrollElementBounds;
  /**
   * The inverse scales to be applied to undo any scaling applied by playback.
   */
  inverseScales: InverseScales;
  /**
   * The zIndex offset for this marker. The marker's index is pageBounds.zIndex + zIndexOffset.
   */
  zIndexOffset: number;
}

export const MARKER_HEIGHT = 20;
export const MARKER_LINE_HEIGHT = MARKER_HEIGHT / 16;

/**
 * The controller for the Scroll Maps Marker responsible for displaying how many page views reached a given depth.
 */
export const ScrollMapsMarkerContainer: React.FC<ScrollMapsMarkerContainerProps> = ({
  colorMap,
  facetData,
  pageBounds,
  overlayContainerBounds,
  playbackScrollElementBounds,
  inverseScales,
  zIndexOffset,
}) => {
  const { scrollElementTop, viewportHeight, containerHeight } = playbackScrollElementBounds;

  // The initial marker position will either be 20% from the bottom of the page or
  // the last depth percent where 100% of page views reached that depth.
  const initialMarkerPosition = useMemo((): number => {
    let maxPageViewScrollDepthPercent = 0;
    while (maxPageViewScrollDepthPercent < 101) {
      if (facetData.depthCounts[maxPageViewScrollDepthPercent] != facetData.totalPageViews) {
        // If not all page views reached this depth percentage, this means that the last
        // percent where all page views reached was the one immediately before this.
        maxPageViewScrollDepthPercent--;
        break;
      } else {
        maxPageViewScrollDepthPercent++;
      }
    }

    // Convert the depth percent to actual pixels. maxPageViewScrollDepthPercent is a percentage stored as an integer
    // (0 - 100). Hence, we need to divide by 100 to perform the calculation appropriately.
    let maxPageViewDepthHeightPx = (pageBounds.height * maxPageViewScrollDepthPercent) / 100;

    // Don't place at the maxPageViewDepthHeightPx if it is out of the screen.
    const maxPosition = viewportHeight * 0.8;

    // The document element top starts at 0. As it is scrolled down, the top becomes negative. Hence, multiplying
    // pageTop by -1 will give how far down the page was scrolled thus far.
    // If we have already scrolled passed maxPageViewDepthHeightPx, simply return maxPosition.
    if (pageBounds.top * -1 > maxPageViewDepthHeightPx) {
      return maxPosition;
    }
    maxPageViewDepthHeightPx = maxPageViewDepthHeightPx + pageBounds.top;
    return Math.min(maxPageViewDepthHeightPx, maxPosition);
  }, [
    facetData.depthCounts,
    facetData.totalPageViews,
    pageBounds.height,
    pageBounds.top,
    viewportHeight,
  ]);

  const [markerPosition, setMarkerPosition] = useState(initialMarkerPosition);
  const [currentMarkerWidth, setCurrentMarkerWidth] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragY, setDragY] = useState<number | null>(null);

  // Determine the marker box width by the number of characters that it will contain. This allows the box width to be somewhat
  // dynamic so that the content fits snuggly but not so dynamic that it changes size by a couple pixels every time a
  // character in the content changes. It is unlikely there will be a case larger than 9 characters. This would only happen
  // if the number of visitors is greater than 999 Billion.
  function getWidth(chars: number): number {
    switch (chars) {
      case 2:
        return 100;
      case 3:
        return 104;
      case 4:
        return 110;
      case 5:
        return 120;
      case 6:
        return 124;
      case 7:
        return 132;
      case 8:
        return 136;
      case 9:
        return 144;
      default:
        return 168;
    }
  }

  function handleDragStart() {
    setDragging(true);
  }

  function handleDrag(y: number) {
    if (dragging) {
      setDragY(y);
    }
  }

  function handleDragEnd() {
    setDragging(false);
  }

  useEffect(() => {
    if (dragY == null) {
      return;
    }
    const scaledOffsets = createScaledOffsets(MARKER_HEIGHT, currentMarkerWidth, inverseScales);
    // Logic copied from ts/ui/playback/slider.ts
    const yPosOffset = dragY - MARKER_LINE_HEIGHT - scaledOffsets.y - overlayContainerBounds.top;
    const yHeightMaxOffset = overlayContainerBounds.height - MARKER_HEIGHT - scaledOffsets.y;
    // The max scaled position a marker can be placed (does not include a top value)
    // The marker position max should be high enough that the bottom half of the marker can still fit.
    const maxPosition = viewportHeight - (MARKER_HEIGHT / 2) * inverseScales.y;
    const currentYPosition = yPosOffset * inverseScales.y;
    const maxHeight = yHeightMaxOffset * inverseScales.y;
    let position = currentYPosition * (maxPosition / maxHeight);

    // Make sure the position is still within the container.
    position = Math.max(position, 0 + (MARKER_HEIGHT / 2) * inverseScales.y);
    position = Math.min(position, maxPosition);
    setMarkerPosition(position);
  }, [
    currentMarkerWidth,
    dragY,
    inverseScales,
    overlayContainerBounds.height,
    overlayContainerBounds.top,
    viewportHeight,
  ]);

  // The top position begins at 0 and becomes negative as the user scrolls down. Hence, we must multiply bounds.top by
  // -1 to get how far we have scrolled down the page.
  let currentDepthPercent = Math.floor(
    (100 * (markerPosition + -1 * scrollElementTop)) / containerHeight,
  );
  // Since the marker position could be negative, do at least 0 to not have an index out of bounds error.
  currentDepthPercent = Math.max(0, currentDepthPercent);

  const currentPageViews = facetData.depthCounts[currentDepthPercent] ?? 0;
  const currentPageViewsShort = shortNumber(currentPageViews);
  const currentPageViewsPercent = Math.floor((currentPageViews * 100) / facetData.totalPageViews);

  const textLen = currentPageViewsPercent.toString().length + currentPageViewsShort.length;
  const markerWidth = getWidth(textLen);
  if (currentMarkerWidth != markerWidth) {
    setCurrentMarkerWidth(markerWidth);
  }

  // Place the top such that the center of the marker is at position. The center of the marker remains the same during scaling.
  // Hence, no offset is necessary.
  const markerTop = markerPosition - MARKER_HEIGHT / 2;
  const scaledOffsets = createScaledOffsets(MARKER_HEIGHT, markerWidth, inverseScales);
  // Place the marker at the right end of the screen.
  const markerLeft =
    pageBounds.right -
    pageBounds.left -
    markerWidth * inverseScales.x -
    scaledOffsets.x * inverseScales.x;
  return (
    // eslint-disable-next-line @fullstory/annotate-react/data-component
    <ScrollMapsMarker
      circleColor={getScrollMapsColorForPageView(colorMap, currentPageViews)}
      pageViews={currentPageViewsShort}
      totalPageViews={shortNumber(facetData.totalPageViews)}
      pageViewPercent={currentPageViewsPercent.toString(10)}
      inverseScales={inverseScales}
      markerBoxBounds={{
        top: markerTop,
        bottom: markerTop + MARKER_HEIGHT * inverseScales.y + scaledOffsets.y * inverseScales.y,
        left: markerLeft,
        right: markerLeft + markerWidth * inverseScales.x + scaledOffsets.x * inverseScales.x,
        height: MARKER_HEIGHT,
        width: markerWidth,
        zIndex: pageBounds.zIndex + zIndexOffset,
      }}
      dragProperties={{
        dragging,
        onDragStart: handleDragStart,
        onDrag: handleDrag,
        onDragEnd: handleDragEnd,
      }}
      tooltipPlacement="top"
    />
  );
};
